import React, { useState } from "react";
import { CasLoader } from "../brand/CasLoader";
import { useAgentProvider } from "../../hook/agentProvider";

// The chat's swap control. It writes the ACCOUNT setting through the same hook Settings uses, so a
// swap here shows in Settings and vice versa; the model dropdown beside it follows.
export function ProviderPicker() {
  const { provider, chooseProvider, status, loaded } = useAgentProvider();
  // Same rule as Settings: Claude is only offered when a login can serve a chat.
  const claudeReady = status.claudeState === "valid" || status.claudeState === "expiring";
  const claudeBlocked = loaded && !claudeReady;
  // Until the Account row is read nothing is known about the login: Claude cannot be chosen yet, and says so.
  const claudeWaiting = !loaded;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const choose = async (next: "openrouter" | "anthropic") => {
    if (busy || next === provider) return;
    setBusy(true);
    setError("");
    try {
      await chooseProvider(next);
    } catch (e: any) {
      // The chat is not Settings; this picker must say what failed, not rely on a page the user
      // cannot see. It keeps the last known provider so the buttons still match the account.
      setError(e?.message ?? "Could not change the provider.");
    } finally {
      setBusy(false);
    }
  };

  const style = (active: boolean): React.CSSProperties => ({
    fontSize: 11,
    background: active ? "rgba(128,128,128,0.25)" : "transparent",
    border: "1px solid rgba(128,128,128,0.4)",
    borderRadius: 3,
    padding: "2px 6px",
    color: "inherit",
    cursor: busy ? "default" : "pointer",
    fontWeight: active ? 600 : 400,
  });

  return (
    <>
      <div
        className="provider-picker"
        role="group"
        aria-label="Provider"
        data-testid="provider-picker"
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <button
          type="button"
          data-testid="provider-openrouter"
          aria-pressed={provider === "openrouter"}
          disabled={busy}
          onClick={() => void choose("openrouter")}
          style={style(provider === "openrouter")}
        >
          OpenRouter
        </button>
        <button
          type="button"
          data-testid="provider-claude"
          data-state={claudeWaiting ? "loading" : status.claudeState}
          aria-pressed={provider === "anthropic"}
          aria-busy={claudeWaiting}
          disabled={busy || claudeWaiting || claudeBlocked}
          title={claudeWaiting ? "Checking your Claude connection" : claudeBlocked ? "Connect Claude in Settings first" : undefined}
          onClick={() => void choose("anthropic")}
          style={style(provider === "anthropic")}
        >
          Claude
          {claudeWaiting && <CasLoader size={10} sx={{ ml: 0.5 }} data-testid="provider-claude-loading" />}
        </button>
      </div>
      {error && (
        <span role="alert" data-testid="provider-picker-error" style={{ fontSize: 11, color: "#c62828" }}>
          {error}
        </span>
      )}
    </>
  );
}
